// src/Pages/Admin/Referrals/ReferralDetailsView.js
import React, { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Tooltip,
  Stack,
  Alert,
  Skeleton,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  People,
  Delete,
  Visibility,
  Search,
  MoreVert,
  CheckCircle,
  Schedule,
  Cancel,
  Refresh,
  Clear,
  PersonAdd,
  AttachMoney,
  Lock,
  LockOpen,
  Share,
  Settings,
  History
} from '@mui/icons-material';
import client from '../../../../../API';
import { toast } from 'sonner';
import RetroactiveReferralDialog from '../adminComponents/reverseRefer';

// Import modularized components
import { COMMISSION_TYPES } from '../newUserParntershipComponents/refferralsuite/commisiontypeconfig';
import { 
  AccessGrantsDialog, 
  CreatePartnerAccessDialog 
} from '../newUserParntershipComponents/refferralsuite/accessgrantdialogs';
import { 
  AccessGrantsListView, 
  ReferralDetailDialog 
} from '../newUserParntershipComponents/refferralsuite/referraldialogs';

// =================== MAIN COMPONENT ===================
const ReferralDetailsView = () => {
  const [referralData, setReferralData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [accessFilter, setAccessFilter] = useState('all');
  const [actionMenu, setActionMenu] = useState(null);
  const [detailDialog, setDetailDialog] = useState({ open: false, referralId: null });
  const [accessGrantsDialog, setAccessGrantsDialog] = useState({ open: false, referralId: null });
  const [accessListDialog, setAccessListDialog] = useState(false);
  const [retroactiveDialog, setRetroactiveDialog] = useState(false);
  const [createAccessDialog, setCreateAccessDialog] = useState(false);

  const queryClient = useQueryClient();

  const { data: apiResponse, isLoading, isError, error, refetch } = useQuery(
    'referralsAndCommissions',
    () => client.commissions.getAll(),
    {
      onSuccess: (response) => {
        const referrals = response?.referrals || [];
        setReferralData(referrals);
        toast.success('Data loaded successfully', {
          description: `${referrals.length} referrals loaded`
        });
      },
      onError: (error) => {
        toast.error('Failed to load data', {
          description: error?.message || 'Please try again'
        });
      }
    }
  );

  const revokeAccessMutation = useMutation(
    ({ referralId }) => client.globalUsers.revokeReferralAccess({ referral_id: referralId }),
    {
      onSuccess: () => {
        toast.success('Access revoked successfully');
        queryClient.invalidateQueries('referralsAndCommissions');
      },
      onError: (error) => {
        toast.error(error?.message || 'Failed to revoke access');
      }
    }
  );

  const deleteReferralMutation = useMutation((id) => client.referrals.delete(id), {
    onSuccess: () => {
      toast.success('Referral deleted successfully');
      queryClient.invalidateQueries('referralsAndCommissions');
      setActionMenu(null);
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to delete referral');
    }
  });

  const summaryStats = useMemo(() => {
    const totalReferrals = referralData.length;
    const pendingReferrals = referralData.filter((r) => r.status === 'pending').length;
    const completedReferrals = referralData.filter((r) => r.status === 'completed').length;
    const referralsWithAccess = referralData.filter((r) => r.access?.has_commission_rights).length;
    const totalPotentialCommissions = referralData.reduce(
      (sum, r) => sum + (r.potential_commission || 0),
      0
    );

    return {
      totalReferrals,
      pendingReferrals,
      completedReferrals,
      referralsWithAccess,
      totalPotentialCommissions,
      conversionRate: totalReferrals > 0 ? ((completedReferrals / totalReferrals) * 100).toFixed(1) : 0
    };
  }, [referralData]);

  const filteredReferralData = useMemo(() => {
    return referralData.filter((referral) => {
      const matchesSearch =
        searchTerm === '' ||
        Object.values(referral).some(
          (value) => value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesStatus = statusFilter === 'all' || referral.status === statusFilter;
      const matchesType = typeFilter === 'all' || referral.referral_type === typeFilter;
      const matchesAccess =
        accessFilter === 'all' ||
        (accessFilter === 'with_access' && referral.access?.has_commission_rights) ||
        (accessFilter === 'no_access' && !referral.access?.has_commission_rights);

      return matchesSearch && matchesStatus && matchesType && matchesAccess;
    });
  }, [referralData, searchTerm, statusFilter, typeFilter, accessFilter]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const getStatusChip = (status) => {
    const config = {
      pending: { color: 'warning', icon: <Schedule fontSize="small" /> },
      approved: { color: 'info', icon: <CheckCircle fontSize="small" /> },
      completed: { color: 'success', icon: <CheckCircle fontSize="small" /> },
      rejected: { color: 'error', icon: <Cancel fontSize="small" /> }
    };
    const c = config[status] || config.pending;
    return <Chip icon={c.icon} label={status} color={c.color} size="small" variant="outlined" />;
  };

  if (isLoading) {
    return (
      <Box sx={{ padding: 3 }}>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error" action={<Button onClick={() => refetch()}>Retry</Button>}>
          Failed to load data: {error?.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3, bgcolor: 'grey.50', minHeight: '100vh' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Referral Management Dashboard
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Track, manage, and share commission rights with multi-type configuration
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <IconButton onClick={() => refetch()} color="primary">
            <Refresh />
          </IconButton>
          <Button variant="outlined" startIcon={<History />} onClick={() => setAccessListDialog(true)}>
            View All Access
          </Button>
          <Button variant="outlined" startIcon={<PersonAdd />} onClick={() => setRetroactiveDialog(true)}>
            Assign Referral
          </Button>
          <Button
            variant="contained"
            startIcon={<Share />}
            onClick={() => setCreateAccessDialog(true)}
          >
            Create Partner Access
          </Button>
        </Stack>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" fontWeight="medium">
                    Total Referrals
                  </Typography>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {summaryStats.totalReferrals}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                  <People />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" fontWeight="medium">
                    With Access
                  </Typography>
                  <Typography variant="h4" color="info.main" fontWeight="bold">
                    {summaryStats.referralsWithAccess}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.light', color: 'info.main' }}>
                  <LockOpen />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" fontWeight="medium">
                    Potential Value
                  </Typography>
                  <Typography variant="h5" color="warning.main" fontWeight="bold">
                    {formatCurrency(summaryStats.totalPotentialCommissions)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.main' }}>
                  <AttachMoney />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" fontWeight="medium">
                    Completed
                  </Typography>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {summaryStats.completedReferrals}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.light', color: 'success.main' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={3}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" fontWeight="medium">
                    Conversion
                  </Typography>
                  <Typography variant="h4" color="secondary.main" fontWeight="bold">
                    {summaryStats.conversionRate}%
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.main' }}>
                  <TrendingUp />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} label="Type">
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="company">Company</MenuItem>
                <MenuItem value="creator">Creator</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Access</InputLabel>
              <Select value={accessFilter} onChange={(e) => setAccessFilter(e.target.value)} label="Access">
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="with_access">With Access</MenuItem>
                <MenuItem value="no_access">No Access</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Clear />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setTypeFilter('all');
                setAccessFilter('all');
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Contact</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Access</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReferralData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>
                          {(item.first_name?.[0] || '').toUpperCase()}
                          {(item.last_name?.[0] || '').toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {item.first_name} {item.last_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {item.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.referral_type}
                        size="small"
                        color={item.referral_type === 'company' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>{getStatusChip(item.status)}</TableCell>
                    <TableCell>
                      {item.access?.has_commission_rights ? (
                        <Tooltip title={`${item.access.commission_percentage}% for ${item.access.term_months} months`}>
                          <Chip 
                            icon={item.access.is_all_access ? <Share fontSize="small" /> : <LockOpen fontSize="small" />} 
                            label={item.access.is_all_access ? "All-Access" : "Active"} 
                            size="small" 
                            color="success" 
                          />
                        </Tooltip>
                      ) : (
                        <Chip icon={<Lock fontSize="small" />} label="None" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {item.access?.has_commission_rights ? (
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {item.access.commission_percentage}%
                          </Typography>
                          {item.potential_commission && (
                            <Typography variant="caption" color="success.main">
                              {formatCurrency(item.potential_commission)}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setDetailDialog({ open: true, referralId: item.id })}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Manage Access">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setAccessGrantsDialog({ open: true, referralId: item.id })}
                          >
                            <Settings fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More">
                          <IconButton size="small" onClick={(e) => setActionMenu({ anchorEl: e.currentTarget, item })}>
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredReferralData.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Dialogs */}
      <ReferralDetailDialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, referralId: null })}
        referralId={detailDialog.referralId}
      />

      <AccessGrantsDialog
        open={accessGrantsDialog.open}
        onClose={() => setAccessGrantsDialog({ open: false, referralId: null })}
        referralId={accessGrantsDialog.referralId}
      />

      <AccessGrantsListView
        open={accessListDialog}
        onClose={() => setAccessListDialog(false)}
      />

      <RetroactiveReferralDialog
        open={retroactiveDialog}
        onClose={() => setRetroactiveDialog(false)}
        onSuccess={() => queryClient.invalidateQueries('referralsAndCommissions')}
      />

      <CreatePartnerAccessDialog
        open={createAccessDialog}
        onClose={() => setCreateAccessDialog(false)}
        referrals={referralData}
        onSuccess={() => queryClient.invalidateQueries('referralsAndCommissions')}
      />

      {/* Action Menu */}
      <Menu anchorEl={actionMenu?.anchorEl} open={Boolean(actionMenu)} onClose={() => setActionMenu(null)}>
        {actionMenu?.item?.access?.has_commission_rights && (
          <MenuItem
            onClick={() => {
              if (window.confirm('Revoke access?')) {
                revokeAccessMutation.mutate({ referralId: actionMenu.item.id });
              }
              setActionMenu(null);
            }}
          >
            <ListItemIcon>
              <Lock fontSize="small" />
            </ListItemIcon>
            <ListItemText>Revoke Access</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            if (window.confirm('Delete referral?')) {
              deleteReferralMutation.mutate(actionMenu.item.id);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ReferralDetailsView;